'use client';

import { c as _c } from "react/compiler-runtime";
import type { ExchangeStatus } from '@morfi/onramp-sdk';
import { useTranslations } from 'next-intl';
import { type ChangeEvent, type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { InputField } from '@/components/ui/input-field';
interface VerificationProps {
  active: boolean;
  busy: boolean;
  challenge: ExchangeStatus['challenge'];
  onConfirm(code: string): Promise<void>;
  onResend(): Promise<void>;
}
export function ExchangeVerification(props) {
  const $ = _c(3);
  const t0 = `${props.active}:${props.challenge?.expiresAt}`;
  let t1;
  if ($[0] !== props || $[1] !== t0) {
    t1 = <VerificationForm key={t0} {...props} />;
    $[0] = props;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function VerificationForm(t0) {
  const $ = _c(52);
  const {
    active,
    busy,
    challenge,
    onConfirm,
    onResend
  } = t0;
  const t = useTranslations("onramp");
  const [code, setCode] = useState("");
  const [now, setNow] = useState(Date.now);
  let t1;
  if ($[0] !== challenge?.expiresAt || $[1] !== challenge?.resendAvailableAt) {
    t1 = () => {
      const timer = setTimeout(() => {
        setCode("");
        setNow(Date.now());
      }, Math.max(0, Date.parse(challenge?.expiresAt ?? "") - Date.now()));
      const resendTimer = setTimeout(() => setNow(Date.now()), Math.max(0, Date.parse(challenge?.resendAvailableAt ?? "") - Date.now()));
      return () => {
        clearTimeout(timer);
        clearTimeout(resendTimer);
      };
    };
    $[0] = challenge?.expiresAt;
    $[1] = challenge?.resendAvailableAt;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = challenge?.expiresAt;
  const t3 = challenge?.resendAvailableAt;
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = [t2, t3];
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t1, t4);
  const expired = !challenge || Date.parse(challenge.expiresAt) <= now;
  let t5;
  if ($[6] !== code || $[7] !== onConfirm) {
    t5 = event => {
      event.preventDefault();
      const input = code;
      setCode("");
      onConfirm(input);
    };
    $[6] = code;
    $[7] = onConfirm;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== expired || $[10] !== t) {
    t6 = expired ? t("exchange_challenge_expired") : t("exchange_verification_description");
    $[9] = expired;
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <p className="text-body text-foreground-secondary">{t6}</p>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== challenge || $[15] !== t) {
    t8 = challenge?.attemptsRemaining !== null && challenge?.attemptsRemaining !== undefined ? <p className="text-caption">{t("exchange_attempts", {
        count: challenge.attemptsRemaining
      })}</p> : null;
    $[14] = challenge;
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t) {
    t9 = t("exchange_code");
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = !active || busy || expired;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = event_0 => setCode(event_0.target.value.replace(/\D/g, ""));
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== code || $[21] !== t10 || $[22] !== t9) {
    t12 = <InputField label={t9} type="password" inputMode="numeric" autoComplete="off" maxLength={7} value={code} disabled={t10} onChange={t11} />;
    $[20] = code;
    $[21] = t10;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== active || $[25] !== busy || $[26] !== challenge?.attemptsRemaining || $[27] !== challenge?.codeLengths || $[28] !== code || $[29] !== expired) {
    t13 = !active || busy || expired || challenge?.attemptsRemaining === 0 || !challenge?.codeLengths.includes(code.length);
    $[24] = active;
    $[25] = busy;
    $[26] = challenge?.attemptsRemaining;
    $[27] = challenge?.codeLengths;
    $[28] = code;
    $[29] = expired;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t) {
    t14 = t("exchange_verify");
    $[31] = t;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== busy || $[34] !== t13 || $[35] !== t14) {
    t15 = <AnimatedButton type="submit" loading={busy} disabled={t13}>{t14}</AnimatedButton>;
    $[33] = busy;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const t16 = !active || busy || expired || !challenge || Date.parse(challenge.resendAvailableAt) > now;
  let t17;
  if ($[37] !== onResend) {
    t17 = () => {
      setCode("");
      onResend();
    };
    $[37] = onResend;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t) {
    t18 = t("exchange_resend");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t16 || $[42] !== t17 || $[43] !== t18) {
    t19 = <AnimatedButton type="button" variant="outline" disabled={t16} onClick={t17}>{t18}</AnimatedButton>;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t12 || $[46] !== t15 || $[47] !== t19 || $[48] !== t5 || $[49] !== t7 || $[50] !== t8) {
    t20 = <form className="rr-block flex flex-col gap-3" onSubmit={t5}>{t7}{t8}{t12}{t15}{t19}</form>;
    $[45] = t12;
    $[46] = t15;
    $[47] = t19;
    $[48] = t5;
    $[49] = t7;
    $[50] = t8;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  return t20;
}